import {
  ReactNode,
  useRef,
  useState,
  MouseEvent,
  CSSProperties,
  memo,
  MutableRefObject,
} from 'react';
import { createPortal } from 'react-dom';

import { cx } from '@libs/classnames';

import styles from './AnimatedNode.module.css';

export type Props = {
  tag?: React.ElementType;
  animation?:
    | 'opacity'
    | 'half-opacity'
    | 'scale'
    | 'none'
    | 'bounce-up'
    | 'change-table'
    | 'crossfade-synced';
  className?: string;
  persist?: boolean;
  initial?: boolean;
  portal?: Element;
  children?: ReactNode;
  attributes?: object;
  style?: CSSProperties;
  nodeRef?: MutableRefObject<HTMLElement | null>;
  onClick?: (e: MouseEvent) => void;
};

const IDENTITY = (e: ReactNode) => e;

export const AnimatedNode = memo(function AnimatedNode({
  tag,
  animation = 'opacity',
  className,
  persist,
  initial,
  portal,
  children,
  attributes,
  style,
  nodeRef,
  onClick,
}: Props) {
  const disabled = animation === 'none';
  const prevChildren = useRef(initial ? undefined : children);
  const memoizedChildren = useRef(children);
  const enterRef = useRef(false);
  const animating = useRef(false);
  const [, forceUpdate] = useState(false);

  const hasChildren = children || children === 0;

  if (!disabled && hasChildren && !prevChildren.current) {
    enterRef.current = true;
  }
  if (!hasChildren) {
    enterRef.current = false;
  }

  const leave = Boolean(!disabled && prevChildren.current && !hasChildren);

  if (hasChildren) {
    memoizedChildren.current = children;
  }
  prevChildren.current = children;

  const HtmlTagname = tag ?? 'div';

  const wrap = portal ? (el: ReactNode) => createPortal(el, portal) : IDENTITY;

  if (!hasChildren && !leave && !animating.current) {
    if (persist) {
      return wrap(
        <HtmlTagname className={className} style={style} onClick={onClick} {...attributes} />,
      );
    }
    return null;
  }

  const content = hasChildren ? children : memoizedChildren.current;
  const hidden = leave || (animating.current && !hasChildren);

  const handleAnimationStart = (e: React.AnimationEvent) => {
    if (e.animationName.includes('anim-')) {
      animating.current = true;
      e.target?.addEventListener('animationcancel', handleAnimationCancel);
    }
  };
  const handleAnimationEnd = (e: React.AnimationEvent) => {
    if (e.animationName.includes('anim-')) {
      animating.current = false;
      if (!hasChildren) {
        forceUpdate((v) => !v);
      }
    }
  };
  const handleAnimationCancel = (e: Event) => {
    handleAnimationEnd(e as unknown as React.AnimationEvent);
    e.target?.removeEventListener('animationcancel', handleAnimationCancel);
  };

  return wrap(
    <HtmlTagname
      className={cx(className, {
        [styles.enter]: enterRef.current,
        [styles.leave]: leave,
      })}
      style={style}
      onClick={onClick}
      onAnimationStart={handleAnimationStart}
      onAnimationEnd={handleAnimationEnd}
      data-animation={animation}
      aria-hidden={hidden}
      inert={hidden}
      {...({ ref: nodeRef } as object)}
      {...attributes}
    >
      {content}
    </HtmlTagname>,
  );
});

export type NodeInfo = {
  key: string | number;
} & Props;
export type NodesProps = {
  children?: Array<NodeInfo | null | undefined> | null | false;
};

export function AnimatedDynamicNodes({ children }: NodesProps) {
  const historyRef = useRef(children);

  const render = new Map<string | number, ReactNode>();

  if (children) {
    children.forEach((props) => {
      if (!props) return;
      render.set(props.key, <AnimatedNode {...props} key={props.key} />);
    });
  }

  if (historyRef.current) {
    historyRef.current.forEach((props) => {
      if (!props || render.has(props.key)) return;
      render.set(
        props.key,
        // eslint-disable-next-line react/no-children-prop
        <AnimatedNode {...props} key={props.key} children={null} />,
      );
    });
  }

  historyRef.current = children;

  return (
    <>{[...render.entries()].map((e) => e[1] ?? <AnimatedNode key={e[0]}>{null}</AnimatedNode>)}</>
  );
}
